/**
 * Send Study Dialog — send a study to a DICOM destination.
 *
 * Lists users with DICOM settings as potential destinations.
 * Uses sendStudy JSON-RPC method which creates a DicomSender job.
 */
import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, List, ListItemButton, ListItemText,
  Box, CircularProgress, Typography, InputAdornment,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { api } from '../../api/rpc';
import type { Study, User } from '../../api/types';
import { gwtStyles } from '../../config/theme';
import { pushError, pushSuccess } from '../../stores/notificationStore';

interface SendStudyDialogProps {
  open: boolean;
  studies: Study[];
  onClose: () => void;
  onComplete?: () => void;
}

export const SendStudyDialog = ({ open, studies, onClose, onComplete }: SendStudyDialogProps) => {
  const [users, setUsers] = useState<User[]>([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setFilter('');
    setSelectedUser(null);
    api.getUsersPaged(0, 500)
      .then((result) => setUsers(result.records))
      .catch((e) => pushError('Failed to load users: ' + (e instanceof Error ? e.message : String(e))))
      .finally(() => setLoading(false));
  }, [open]);

  const filteredUsers = filter.trim()
    ? users.filter((u) =>
        u.name.toLowerCase().includes(filter.toLowerCase()) ||
        u.user.toLowerCase().includes(filter.toLowerCase()),
      )
    : users;

  const handleSend = async () => {
    if (!selectedUser || studies.length === 0) return;
    setSending(true);
    try {
      for (const study of studies) {
        await api.sendStudy(study.objKey, selectedUser.objKey);
      }
      pushSuccess(`Sending ${studies.length} study/studies to ${selectedUser.name}`);
      onComplete?.();
      onClose();
    } catch (e) {
      pushError('Send failed: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '6px',
          border: `1px solid ${gwtStyles.borderSubtle}`,
          backgroundColor: gwtStyles.popupBg,
          maxHeight: '70vh',
        },
      }}
    >
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1 }}>
        Send {studies.length > 1 ? `${studies.length} Studies` : 'Study'}
      </DialogTitle>
      <DialogContent>
        {studies.length === 1 && (
          <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
            {studies[0].name} — {studies[0].acc} ({studies[0].mod})
          </Typography>
        )}
        <TextField
          fullWidth
          size="small"
          placeholder="Filter destinations…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1, '& .MuiInputBase-root': { fontSize: 13 } }}
        />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List
            dense
            sx={{
              maxHeight: 280,
              overflow: 'auto',
              border: `1px solid ${gwtStyles.borderSubtle}`,
              borderRadius: '4px',
            }}
          >
            {filteredUsers.map((user) => (
              <ListItemButton
                key={user.objKey}
                selected={selectedUser?.objKey === user.objKey}
                onClick={() => setSelectedUser(user)}
                sx={{ py: 0.5 }}
              >
                <ListItemText
                  primary={user.name}
                  secondary={`${user.user}${user.subType ? ` · ${user.subType}` : ''}`}
                  primaryTypographyProps={{ fontSize: 13 }}
                  secondaryTypographyProps={{ fontSize: 11 }}
                />
              </ListItemButton>
            ))}
            {filteredUsers.length === 0 && (
              <Typography sx={{ py: 2, textAlign: 'center', fontSize: 12, color: 'text.secondary' }}>
                No destinations found
              </Typography>
            )}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontSize: 12 }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={!selectedUser || sending}
          onClick={handleSend}
          sx={{ textTransform: 'none', fontSize: 12 }}
        >
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
